import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Users, UserPlus, Crown, Trash2, Save, Mail, CheckCircle2, Clock, MailQuestion, Send, ArrowRight, ExternalLink, Loader2, Receipt } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import InvoicesList from "@/components/InvoicesList";
import { useLang } from "@/context/LangContext";

export function MyCompanyBillingBody() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [categories, setCategories] = useState([]);

  const load = () => api.get("/portal/my-company").then((r) => { setData(r.data); setForm(r.data.company || {}); }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {}); }, []);

  const save = async () => {
    try { await api.patch("/portal/my-company", form); toast.success("Entreprise mise à jour"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!data) return <div className="py-20 text-center text-neutral-500">{t("portal_mycompany_90")}</div>;
  const canManage = data.can_manage;

  return (
    <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="billing-block">
      <h2 className="text-2xl font-bold text-navy">{t("portal_mycompany_section_title")}</h2>
      <p className="mt-1 text-xs text-neutral-500">{t("portal_mycompany_03")}<strong>{t("portal_mycompany_name_term")}</strong>{t("portal_mycompany_04")}<strong>{t("portal_mycompany_legal_term")}</strong>{t("portal_mycompany_06")}</p>
      {!canManage && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800" data-testid="billing-readonly-banner">
          {t("portal_mycompany_readonly") || "Vous consultez ce profil en lecture seule. Seul le contact principal (ou un administrateur) peut modifier ces informations."}
        </div>
      )}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">{t("portal_mycompany_name_label")}</label>
          <input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("portal_mycompany_name_placeholder")} disabled={!canManage} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="billing-name" />
          <div className="mt-0.5 text-[10px] text-neutral-400">{t("portal_mycompany_07")}</div>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-neutral-500">{t("portal_mycompany_08")}</label>
          <input value={form.legal_name || ""} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder={t("portal_mycompany_legal_placeholder")} disabled={!canManage} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="billing-legal-name" />
          <div className="mt-0.5 text-[10px] text-neutral-400">{t("portal_mycompany_09")}</div>
        </div>
        <input value={form.siren || ""} onChange={(e) => setForm({ ...form, siren: e.target.value })} placeholder="SIREN" disabled={!canManage} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="billing-siren" />
        <input value={form.vat_number || ""} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder={t("portal_mycompany_vat_placeholder")} disabled={!canManage} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="billing-vat" />
        <input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("portal_mycompany_91")} disabled={!canManage} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="billing-email" />
        <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("portal_mycompany_92")} disabled={!canManage} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="billing-phone" />
      </div>

      <div className="mt-5 rounded-xl border border-wine-100 bg-wine-50/30 p-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-wine-700">Adhésion La WineTech</div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Catégorie</label>
            <select value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} disabled={!canManage} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white" data-testid="billing-category">
              <option value="">— Sélectionner —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.icon ? `${c.icon} ` : ""}{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Chiffre d&apos;affaires annuel (€)</label>
            <input type="number" min="0" step="1000" value={form.annual_revenue ?? ""} onChange={(e) => setForm({ ...form, annual_revenue: e.target.value === "" ? null : Number(e.target.value) })} placeholder="ex: 500000" disabled={!canManage} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white" data-testid="billing-annual-revenue" />
            <div className="mt-0.5 text-[10px] text-neutral-400">Sert au calcul automatique de la cotisation.</div>
          </div>
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-xs text-neutral-700">
          <input type="checkbox" checked={form.auto_renewal !== false} onChange={(e) => setForm({ ...form, auto_renewal: e.target.checked })} disabled={!canManage} className="accent-wine-500" data-testid="billing-renewal" />
          <strong>Renouvellement automatique</strong> de l&apos;adhésion à la prochaine échéance
        </label>
      </div>

      {canManage && (
        <div className="mt-5 flex justify-end">
          <button onClick={save} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="billing-save"><Save className="h-3.5 w-3.5" /> {t("portal_mycompany_96")}</button>
        </div>
      )}

      {/* Sprint 87d — Stripe Customer Portal for self-service billing management */}
      {canManage && <StripeBillingPortalCard />}

      {/* Sprint 92ah — In-app Stripe invoices list (avoids leaving the portal for a quick check) */}
      <div className="mt-5">
        <InvoicesList endpoint="/portal/my-invoices" title={t("inv_my_title")} emptyLabel={t("inv_my_empty")} />
      </div>
    </div>
  );
}

function StripeBillingPortalCard() {
  const [loading, setLoading] = useState(false);
  const open = async () => {
    setLoading(true);
    try {
      const r = await api.post("/portal/billing-portal");
      if (r.data?.url) window.open(r.data.url, "_blank");
      else toast.error("Aucun portail Stripe disponible — réessayez plus tard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Portail indisponible");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="mt-6 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/40 to-violet-50/40 p-5" data-testid="stripe-billing-portal-card">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 grid h-10 w-10 place-items-center rounded-full bg-indigo-500 text-white">
          <Receipt className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold text-indigo-900">Mes factures &amp; abonnement</h3>
          <p className="mt-1 text-xs text-indigo-800/80">
            Téléchargez vos factures, mettez à jour votre moyen de paiement et gérez votre adhésion en libre-service.
            Vous serez redirigé vers le portail sécurisé Stripe.
          </p>
        </div>
        <button
          onClick={open}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
          data-testid="open-stripe-portal-btn"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
          Ouvrir le portail
        </button>
      </div>
    </div>
  );
}


export function MyCompanyBody() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [newCol, setNewCol] = useState({ email: "", first_name: "", last_name: "", job_title: "", phone: "", linkedin: "", calendar_link: "", is_primary: false });
  const [categories, setCategories] = useState([]);
  // Sprint 92am — Projects catalog for the member multi-select.
  const [projectsCatalog, setProjectsCatalog] = useState([]);

  const load = () => api.get("/portal/my-company").then((r) => { setData(r.data); setForm(r.data.company || {}); }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    api.get("/projects").then((r) => setProjectsCatalog(r.data || [])).catch(() => {});
  }, []);

  const save = async () => {
    try { await api.patch("/portal/my-company", form); toast.success("Entreprise mise à jour"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addCol = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/portal/my-company/contacts", newCol);
      toast.success("Collaborateur ajouté" + (res.data.activation_url ? " — email d'activation envoyé" : ""));
      setNewCol({ email: "", first_name: "", last_name: "", job_title: "", phone: "", linkedin: "", calendar_link: "", is_primary: false });
      setAdding(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeCol = async (c) => {
    if (!window.confirm(`Retirer ${c.first_name} ${c.last_name} de l'entreprise ?`)) return;
    try { await api.delete(`/portal/my-company/contacts/${c.id}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const makePrimary = async (c) => {
    try { await api.post(`/portal/my-company/contacts/${c.id}/make-primary`); toast.success("Contact principal mis à jour"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const resendInvite = async (c) => {
    try {
      await api.post(`/portal/my-company/contacts/${c.id}/resend-invite`);
      toast.success("Invitation renvoyée à " + c.email);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const STATUS_META = {
    active: { label: t("portal_mycompany_collab_status_active"), icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700" },
    pending: { label: t("portal_mycompany_collab_status_pending"), icon: Clock, cls: "bg-amber-50 text-amber-700" },
    no_account: { label: t("portal_mycompany_collab_status_no_account"), icon: MailQuestion, cls: "bg-neutral-100 text-neutral-600" },
  };

  if (!data) return <div className="py-20 text-center text-neutral-500">{t("portal_mycompany_90")}</div>;
  const canManage = data.can_manage;

  return (
    <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-navy">{t("portal_mycompany_01")}</h2>
          <p className="mt-1 text-sm text-neutral-600">{t("portal_mycompany_02")}</p>
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="company-block">
          <h3 className="text-lg font-bold text-navy"><Building2 className="mr-1 inline h-4 w-4 text-wine-500" /> {t("portal_mycompany_section_title")}</h3>
          <p className="mt-1 text-xs text-neutral-500">{t("portal_mycompany_03")}<strong>{t("portal_mycompany_name_term")}</strong>{t("portal_mycompany_04")}<strong>{t("portal_mycompany_legal_term")}</strong>{t("portal_mycompany_06")}</p>
          {!canManage && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800" data-testid="my-company-readonly-banner">
              {t("portal_mycompany_readonly") || "Vous consultez ce profil en lecture seule. Seul le contact principal (ou un administrateur) peut modifier ces informations. Demandez à votre contact principal d'activer l'option « Autoriser les autres collaborateurs à modifier » dans cette page."}
            </div>
          )}
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-[10px] font-bold uppercase text-neutral-500">{t("portal_mycompany_name_label")}</label>
              <input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("portal_mycompany_name_placeholder")} disabled={!canManage} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="my-company-name" />
              <div className="mt-0.5 text-[10px] text-neutral-400">{t("portal_mycompany_07")}</div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-neutral-500">{t("portal_mycompany_08")}</label>
              <input value={form.legal_name || ""} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder={t("portal_mycompany_legal_placeholder")} disabled={!canManage} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="my-company-legal-name" />
              <div className="mt-0.5 text-[10px] text-neutral-400">{t("portal_mycompany_09")}</div>
            </div>
            <input value={form.siren || ""} onChange={(e) => setForm({ ...form, siren: e.target.value })} placeholder="SIREN" disabled={!canManage} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
            <input value={form.vat_number || ""} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder={t("portal_mycompany_vat_placeholder")} disabled={!canManage} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
            <input type="url" value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder={t("portal_mycompany_website_placeholder")} disabled={!canManage} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
            <input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("portal_mycompany_91")} disabled={!canManage} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
            <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("portal_mycompany_92")} disabled={!canManage} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
            <input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder={t("portal_mycompany_city_placeholder")} disabled={!canManage} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
            <textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("portal_mycompany_93")} disabled={!canManage} className="sm:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          </div>

          {/* Catégorie + CA + Renouvellement (Sprint 73) */}
          <div className="mt-4 rounded-xl border border-wine-100 bg-wine-50/30 p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-wine-700">Adhésion La WineTech</div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Catégorie</label>
                <select value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} disabled={!canManage} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white" data-testid="my-company-category">
                  <option value="">— Sélectionner —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.icon ? `${c.icon} ` : ""}{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Chiffre d&apos;affaires annuel (€)</label>
                <input type="number" min="0" step="1000" value={form.annual_revenue ?? ""} onChange={(e) => setForm({ ...form, annual_revenue: e.target.value === "" ? null : Number(e.target.value) })} placeholder="ex: 500000" disabled={!canManage} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white" data-testid="my-company-annual-revenue" />
                <div className="mt-0.5 text-[10px] text-neutral-400">Sert au calcul automatique de la cotisation.</div>
              </div>
            </div>
            <label className="mt-3 inline-flex items-center gap-2 text-xs text-neutral-700">
              <input type="checkbox" checked={form.auto_renewal !== false} onChange={(e) => setForm({ ...form, auto_renewal: e.target.checked })} disabled={!canManage} className="accent-wine-500" data-testid="my-company-renewal" />
              <strong>Renouvellement automatique</strong> de l&apos;adhésion à la prochaine échéance
            </label>
          </div>

          {/* Logo + bannière */}
          {canManage && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="my-company-medias">
              <ImageUpload value={form.logo || ""} onChange={(url) => setForm({ ...form, logo: url })} label={t("portal_mycompany_94")} testId="my-company-logo" />
              <ImageUpload value={form.banner || ""} onChange={(url) => setForm({ ...form, banner: url })} label={t("portal_mycompany_95")} testId="my-company-banner" />
            </div>
          )}

          {/* Sprint 92am — Tags + Projects (self-service multi-select) */}
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-neutral-100 bg-neutral-50/50 p-3">
              <label className="text-[11px] font-bold uppercase tracking-wider text-wine-700">🏷️ Tags / expertises</label>
              <input
                value={(form.tags || []).join(", ")}
                onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                placeholder="IoT, Bio, Agroécologie, Export…"
                disabled={!canManage}
                className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                data-testid="my-company-tags"
              />
              <p className="mt-1 text-[10px] text-neutral-500">Virgule pour séparer. Visible dans l'annuaire.</p>
              {(form.tags || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {form.tags.map((t) => (
                    <span key={t} className="rounded-full bg-wine-100 px-2 py-0.5 text-[11px] font-semibold text-wine-700">{t}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-pink-100 bg-pink-50/40 p-3">
              <label className="text-[11px] font-bold uppercase tracking-wider text-pink-700">🚀 Projets WineTech</label>
              <select
                multiple
                value={form.project_ids || []}
                onChange={(e) => setForm({ ...form, project_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                disabled={!canManage}
                size={Math.min(Math.max(projectsCatalog.length, 3), 6)}
                className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm"
                data-testid="my-company-projects"
              >
                {projectsCatalog.length === 0 && <option disabled>— Aucun projet WineTech actif —</option>}
                {projectsCatalog.map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
              <p className="mt-1 text-[10px] text-neutral-500">Ctrl/Cmd + clic pour multi-sélection. Votre entreprise apparaîtra sur la page du projet.</p>
              {(form.project_ids || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {form.project_ids.map((pid) => {
                    const p = projectsCatalog.find((x) => x.id === pid);
                    return p ? (
                      <a key={pid} href={`/projects/${p.slug}`} target="_blank" rel="noreferrer" className="rounded-full bg-pink-100 px-2 py-0.5 text-[11px] font-semibold text-pink-700 hover:bg-pink-200" data-testid={`my-company-project-${pid}`}>
                        🚀 {p.title}
                      </a>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>

          {canManage && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <label className="inline-flex items-center gap-2 text-xs text-neutral-700">
                <input type="checkbox" checked={form.primary_contact_can_manage !== false} onChange={(e) => setForm({ ...form, primary_contact_can_manage: e.target.checked })} className="accent-wine-500" data-testid="primary-only-toggle" />{t("portal_mycompany_10")}</label>
              <button onClick={save} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="my-company-save"><Save className="h-3.5 w-3.5" /> {t("portal_mycompany_96")}</button>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="collaborators-block">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold text-navy"><Users className="mr-1 inline h-4 w-4 text-wine-500" /> {t("portal_mycompany_collaborators_title")} ({data.contacts.length})</h3>
              <p className="mt-1 text-xs text-neutral-500">{t("portal_mycompany_collab_subtitle")}</p>
            </div>
            <button onClick={() => setAdding(true)} className="inline-flex items-center gap-2 rounded-full bg-wine-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-wine-700" data-testid="add-collab-btn"><UserPlus className="h-4 w-4" />{t("portal_mycompany_11")}</button>
          </div>

          {/* Big CTA banner shown when there are few collaborators */}
          {!adding && data.contacts.length < 3 && (
            <button
              onClick={() => setAdding(true)}
              className="mt-4 flex w-full items-center justify-between gap-4 rounded-2xl border-2 border-dashed border-wine-300 bg-gradient-to-r from-wine-50 to-amber-50 px-5 py-4 text-left transition-all hover:border-wine-500 hover:from-wine-100 hover:to-amber-100"
              data-testid="add-collab-cta"
            >
              <div className="flex items-center gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-wine-600 text-white shadow-lg">
                  <UserPlus className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-navy">{t("portal_mycompany_invite_cta_title")}</div>
                  <div className="text-xs text-neutral-600">{t("portal_mycompany_invite_cta_subtitle")}</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-wine-600" />
            </button>
          )}

          {adding && (
            <form onSubmit={addCol} className="mt-4 grid gap-2 rounded-xl border-2 border-dashed border-wine-200 bg-wine-50/30 p-4 sm:grid-cols-2" data-testid="add-collab-form">
              <input required type="email" value={newCol.email} onChange={(e) => setNewCol({ ...newCol, email: e.target.value })} placeholder={t("portal_mycompany_email_required_placeholder")} className="sm:col-span-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="collab-email" />
              <input required value={newCol.first_name} onChange={(e) => setNewCol({ ...newCol, first_name: e.target.value })} placeholder={t("portal_mycompany_97")} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="collab-first" />
              <input required value={newCol.last_name} onChange={(e) => setNewCol({ ...newCol, last_name: e.target.value })} placeholder={t("portal_mycompany_lastname_required_placeholder")} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="collab-last" />
              <input value={newCol.job_title} onChange={(e) => setNewCol({ ...newCol, job_title: e.target.value })} placeholder={t("portal_mycompany_jobtitle_placeholder")} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" />
              <input value={newCol.phone} onChange={(e) => setNewCol({ ...newCol, phone: e.target.value })} placeholder={t("portal_mycompany_92")} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" />
              <input value={newCol.linkedin} onChange={(e) => setNewCol({ ...newCol, linkedin: e.target.value })} placeholder={t("portal_mycompany_linkedin_placeholder")} type="url" className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="collab-linkedin" />
              <input value={newCol.calendar_link} onChange={(e) => setNewCol({ ...newCol, calendar_link: e.target.value })} placeholder={t("portal_mycompany_calendar_placeholder")} type="url" className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="collab-calendar" />
              <label className="inline-flex items-center gap-2 text-xs text-neutral-700 sm:col-span-2">
                <input type="checkbox" checked={newCol.is_primary} onChange={(e) => setNewCol({ ...newCol, is_primary: e.target.checked })} />{t("portal_mycompany_12")}</label>
              <div className="sm:col-span-2 flex gap-2">
                <button type="submit" className="rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white" data-testid="collab-submit"><Mail className="mr-1 inline h-3 w-3" /> {t("portal_mycompany_98")}</button>
                <button type="button" onClick={() => setAdding(false)} className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700">{t("portal_mycompany_99")}</button>
              </div>
            </form>
          )}

          <div className="mt-4 space-y-2">
            {data.contacts.length === 0 && <div className="rounded-lg bg-neutral-50 p-6 text-center text-sm text-neutral-500">{t("portal_mycompany_13")}</div>}
            {data.contacts.map((c) => {
              const s = STATUS_META[c.activation_status] || STATUS_META.no_account;
              const S = s.icon;
              return (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-100 bg-white p-3" data-testid={`collab-row-${c.id}`}>
                <div>
                  <div className="text-sm font-semibold text-neutral-900">
                    {c.first_name} {c.last_name}
                    {c.is_primary_contact && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800"><Crown className="h-2.5 w-2.5" /> {t("portal_mycompany_primary_badge")}</span>}
                    <span className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${s.cls}`} data-testid={`collab-status-${c.id}`}><S className="h-2.5 w-2.5" /> {s.label}</span>
                  </div>
                  <div className="text-xs text-neutral-500">{c.email}{c.job_title ? ` · ${c.job_title}` : ""}</div>
                </div>
                {canManage && (
                  <div className="flex gap-1">
                    {c.activation_status === "pending" && (
                      <button onClick={() => resendInvite(c)} className="inline-flex items-center gap-1 rounded-full border border-wine-200 bg-wine-50 px-3 py-1 text-[10px] font-bold text-wine-700 hover:bg-wine-100" data-testid={`collab-resend-${c.id}`}><Send className="h-2.5 w-2.5" /> {t("portal_mycompany_100")}</button>
                    )}
                    {c.activation_status === "no_account" && (
                      <button onClick={() => resendInvite(c)} className="inline-flex items-center gap-1 rounded-full border border-wine-200 bg-wine-50 px-3 py-1 text-[10px] font-bold text-wine-700 hover:bg-wine-100" data-testid={`collab-invite-${c.id}`}><Mail className="h-2.5 w-2.5" /> {t("portal_mycompany_101")}</button>
                    )}
                    {!c.is_primary_contact && <button onClick={() => makePrimary(c)} className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[10px] font-bold text-amber-800 hover:bg-amber-100" data-testid={`collab-primary-${c.id}`}><Crown className="mr-1 inline h-2.5 w-2.5" />Principal</button>}
                    {!c.is_primary_contact && <button onClick={() => removeCol(c)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" data-testid={`collab-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      </div>
  );
}

export default function MyCompany() {
  return (
    <PortalLayout sidebar="member">
      <MyCompanyBody />
    </PortalLayout>
  );
}
